import { Edit } from "@app/ui";
import { useEffect, useRef, useState } from "react";
import { MarkdownPreview } from "../../../notes/MarkdownPreview.js";
import { hasMarkdownSyntax } from "../../../notes/markdown.js";
import { motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";

interface Props {
  value: string;
  onChange: (value: string) => void;
}

/** Compact mobile notes editor. Empty notes collapse to a small affordance; once
 *  edited, the textarea stays available so long-form details remain reachable.
 *  Tapping "Add notes" focuses the textarea immediately (opens the keyboard). */
export function NotesField({ value, onChange }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const [editing, setEditing] = useState(value.trim().length > 0);
  const justOpened = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const showPreview = value.trim().length > 0 && hasMarkdownSyntax(value);

  useEffect(() => {
    if (editing && justOpened.current) {
      justOpened.current = false;
      const id = requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
      return () => cancelAnimationFrame(id);
    }
  }, [editing]);

  if (!editing) {
    return (
      <motion.div layout={!reduce} transition={reduce ? { duration: 0 } : springs.snappy} className="px-1">
        <button
          type="button"
          onClick={() => { justOpened.current = true; setEditing(true); }}
          className="flex w-full items-center gap-1.5 px-1 py-1 text-left text-sm text-muted-foreground"
        >
          <Edit className="h-4 w-4 shrink-0" />
          {t("taskForm.addNotes")}
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div layout={!reduce} transition={reduce ? { duration: 0 } : springs.snappy} className="px-1">
      <label className="mb-1 block text-xs text-muted-foreground" htmlFor="mobile-task-notes">{t("common.notes")}</label>
      <textarea
        id="mobile-task-notes"
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => { if (!value.trim()) setEditing(false); }}
        rows={3}
        placeholder={t("taskForm.addNotes")}
        className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
      />
      {showPreview && (
        <div className="mt-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          <div className="mb-1 font-medium uppercase tracking-wide">{t("taskForm.markdownPreview")}</div>
          <MarkdownPreview value={value} />
        </div>
      )}
    </motion.div>
  );
}
